import React, { useEffect, useState, useCallback } from 'react';
import {
  getCreditBalance,
  getCreditHistory,
  getCreditPacks,
  checkoutCredits,
} from '../api/credits';

// Credits below which we warn the org to top up (~5 supervised interviews).
const LOW_BALANCE_THRESHOLD = 5;

function fmtCredits(n) {
  if (n == null) return '0';
  const num = Number(n);
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

function fmtPrice(cents, currency) {
  const amount = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch {
    return `${currency || 'USD'} ${amount.toFixed(2)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const ENTRY_BADGE = {
  PURCHASE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  GRANT:    'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COMMIT:   'bg-rose-50 text-rose-700 ring-rose-200',
  HOLD:     'bg-amber-50 text-amber-700 ring-amber-200',
  REFUND:   'bg-sky-50 text-sky-700 ring-sky-200',
  ADJUST:   'bg-slate-100 text-slate-600 ring-slate-200',
};

export default function BillingPage() {
  const [balance, setBalance] = useState(null);
  const [packs, setPacks]     = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [buying, setBuying]   = useState(null); // packId in-flight
  const [notice, setNotice]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [b, p, h] = await Promise.all([
        getCreditBalance(),
        getCreditPacks(),
        getCreditHistory(0, 25),
      ]);
      setBalance(b.data);
      setPacks(p.data || []);
      setHistory(h.data?.content || []);
    } catch (err) {
      setError(err.message || 'Failed to load billing data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleBuy = useCallback(async (pack) => {
    setBuying(pack.id);
    setNotice('');
    setError('');
    try {
      const res = await checkoutCredits(pack.id); // provider defaults to MANUAL
      if (res.data?.redirectUrl) {
        window.location.href = res.data.redirectUrl;
        return;
      }
      setNotice(res.data?.message
        || 'Purchase started. An admin will confirm it to add credits to your balance.');
    } catch (err) {
      // 501 = provider not wired yet; 402/others surface their message.
      setError(err.message || 'Could not start the purchase.');
    } finally {
      setBuying(null);
    }
  }, []);

  const available = Number(balance?.available ?? 0);
  const held      = Number(balance?.held ?? 0);
  const settled   = Number(balance?.balance ?? 0);
  const low       = available < LOW_BALANCE_THRESHOLD;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Billing &amp; Credits</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Pay-as-you-go. Each interview spends credits: supervised = 1, AI interview = 2.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200
                     rounded-lg hover:bg-slate-50 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-sm px-4 py-3">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Balance cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={`card p-5 ${low ? 'border-amber-300 bg-amber-50/50' : ''}`}>
              <p className="eyebrow">Available</p>
              <p className={`mt-1 font-heading text-3xl font-bold ${low ? 'text-amber-700' : 'text-ink'}`}>
                {fmtCredits(available)}
              </p>
              {low && (
                <p className="mt-1 text-xs text-amber-700 font-medium">
                  Low balance — top up to keep running interviews.
                </p>
              )}
            </div>
            <div className="card p-5">
              <p className="eyebrow">Reserved (held)</p>
              <p className="mt-1 font-heading text-3xl font-bold text-ink">{fmtCredits(held)}</p>
              <p className="mt-1 text-xs text-ink-muted">Held for in-progress interviews.</p>
            </div>
            <div className="card p-5">
              <p className="eyebrow">Settled balance</p>
              <p className="mt-1 font-heading text-3xl font-bold text-ink">{fmtCredits(settled)}</p>
              <p className="mt-1 text-xs text-ink-muted">Total credits owned.</p>
            </div>
          </div>

          {/* Packs */}
          <div>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Buy credits</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {packs.length === 0 && (
                <p className="text-sm text-slate-500">No packs available right now.</p>
              )}
              {packs.map((pack) => (
                <div key={pack.id} className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col">
                  <p className="text-2xl font-bold text-slate-900">{fmtCredits(pack.credits)} credits</p>
                  <p className="text-sm text-slate-500 mt-0.5">{fmtPrice(pack.priceCents, pack.currency)}</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    ≈ {fmtPrice(Math.round(pack.priceCents / Number(pack.credits)), pack.currency)} / credit
                  </p>
                  <button
                    onClick={() => handleBuy(pack)}
                    disabled={buying === pack.id}
                    className="btn btn-primary mt-4 w-full"
                  >
                    {buying === pack.id ? 'Starting…' : 'Buy'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* History */}
          <div>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Usage history</h2>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {history.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-500">No credit activity yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                      <th className="px-4 py-2.5 font-semibold">Type</th>
                      <th className="px-4 py-2.5 font-semibold">Description</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Credits</th>
                      <th className="px-4 py-2.5 font-semibold text-right">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((e) => {
                      const delta = Number(e.delta);
                      return (
                        <tr key={e.id} className="border-b border-slate-50 last:border-0">
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ring-1
                                             ${ENTRY_BADGE[e.entryType] || 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
                              {e.entryType}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-slate-600">{e.description || '—'}</td>
                          <td className={`px-4 py-2.5 text-right font-mono font-semibold
                                          ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {delta >= 0 ? '+' : ''}{fmtCredits(delta)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-400 text-xs">{fmtDate(e.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
